// The one seam worth preserving (spec s3).
//
//   [ audio source ]  ->  16 kHz mono PCM  ->  [ pipeline ]
//
// The audio source is the only code that knows LiveSwitch exists. If a
// LiveSwitch Connection or AudioStream ever appears downstream of this
// interface, the seam has been broken.
//
// Frames are 16-bit signed PCM, 16 kHz, mono, 20 ms per frame (320 samples).
// Timestamps are milliseconds since session start, monotonic.

export const SAMPLE_RATE = 16000;
export const FRAME_SAMPLES = 320; // 20 ms at 16 kHz
export const FRAME_MS = (FRAME_SAMPLES / SAMPLE_RATE) * 1000;

export type FrameHandler = (pcm: Int16Array, timestampMs: number) => void;
export type ErrorHandler = (err: Error) => void;

export interface AudioSource {
  start(): Promise<void>;
  stop(): Promise<void>;
  onFrame(handler: FrameHandler): void;
  onError(handler: ErrorHandler): void;
}

/** Shared fan-out plumbing so each source only implements start/stop. */
export abstract class BaseAudioSource implements AudioSource {
  private frameHandlers: FrameHandler[] = [];
  private errorHandlers: ErrorHandler[] = [];

  abstract start(): Promise<void>;
  abstract stop(): Promise<void>;

  onFrame(handler: FrameHandler): void {
    this.frameHandlers.push(handler);
  }

  onError(handler: ErrorHandler): void {
    this.errorHandlers.push(handler);
  }

  protected emitFrame(pcm: Int16Array, timestampMs: number): void {
    for (const handler of this.frameHandlers) handler(pcm, timestampMs);
  }

  protected emitError(err: Error): void {
    if (this.errorHandlers.length === 0) {
      console.error('[audio] unhandled source error', err);
      return;
    }
    for (const handler of this.errorHandlers) handler(err);
  }
}
